"use client";

import { ShoppingCart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";

import { brandImagePaths } from "@/lib/catalog/catalog-image";
import { subtleTween } from "@/components/ui/motion-primitives";

import { BottomNavigationActiveItem } from "@/components/catalog/bottom-navigation";
import { CatalogSearchForm } from "@/components/catalog/catalog-search-form";

export interface TopAppBarProps {
  readonly cartCount: number;
  readonly activeItem?: BottomNavigationActiveItem;
  readonly searchDefaultValue?: string;
}

export function TopAppBar({ cartCount, activeItem, searchDefaultValue }: Readonly<TopAppBarProps>) {
  return (
    <header className="sticky top-0 z-40 bg-background px-4 shadow-sm">
      <div className="mx-auto flex h-[54px] w-full max-w-6xl items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <Link className="inline-flex min-h-10 items-center" href="/" prefetch={false}>
            <Image
              alt="Impulsa Impresiones Bolivia"
              className="object-contain"
              height={36}
              priority
              src={brandImagePaths.logo}
              width={135}
            />
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm font-extrabold">
            <Link
              href="/"
              prefetch={false}
              className={`py-1 transition-colors hover:text-primary ${
                activeItem === "home" ? "text-primary border-b-2 border-primary" : "text-muted"
              }`}
            >
              Inicio
            </Link>
            <Link
              href="/categorias"
              prefetch={false}
              className={`py-1 transition-colors hover:text-primary ${
                activeItem === "categories" ? "text-primary border-b-2 border-primary" : "text-muted"
              }`}
            >
              Categorías
            </Link>
            <Link
              href="/ubicacion"
              prefetch={false}
              className={`py-1 transition-colors hover:text-primary ${
                activeItem === "location" ? "text-primary border-b-2 border-primary" : "text-muted"
              }`}
            >
              Ubicación
            </Link>
          </nav>
        </div>

        {/* Buscador integrado en el header para desktop */}
        <div className="hidden md:block flex-1 max-w-md mx-4">
          <CatalogSearchForm defaultValue={searchDefaultValue} className="w-full" />
        </div>

        <Link
          aria-label="Ver carrito"
          className="relative inline-flex min-h-10 min-w-10 items-center justify-center text-primary"
          href="/pedido"
          prefetch={false}
        >
          <ShoppingCart aria-hidden="true" size={25} strokeWidth={2.5} />
          <AnimatePresence>
            {cartCount > 0 ? (
              <motion.span
                animate={{ opacity: 1, scale: 1 }}
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-secondary px-1 text-[10px] font-bold text-white"
                exit={{ opacity: 0, scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.9 }}
                key="top-cart-count"
                transition={subtleTween}
              >
                {cartCount}
              </motion.span>
            ) : null}
          </AnimatePresence>
        </Link>
      </div>
    </header>
  );
}
